/* Plan and billing · the school's real subscription. Design: ConsoleBilling.dc.html.

   The plan, cycle, renewal date and add-ons come from the organization doc
   (useOrgSubscription + org.raw), and every price is worked out by the same
   subscriptionModel.bill() the classic SubscriptionManager charges with.
   Moving up, cancelling, resuming and adding a branch run here through the
   payments backend (BillingModals, 2026-10-28); a move down, a new cycle or an
   add-on is a request to Trasse, because only Trasse may write those. "Pay
   early" opens /pay. The payments backend has no invoice list, so none is shown. */
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { BranchOrgRow, listBranches, resumeSubscription, switchBranch } from '../../services/paymentsApi';
import { AskTrasseModal, BranchModal, CancelModal, UpgradeModal } from '../BillingModals';
import { useFleet } from '../data/FleetData';
import { usePage } from '../Shell';
import { useOrgSubscription } from '../../services/useOrgSubscription';
import {
  ADDON_PRICE, AccountUnit, BillingPeriod, PLAN_DEFS, PlanId, SELLABLE_PLAN_DEFS, bill, billedWord, perUnitSuffix, rate, totalSuffix, unitWord,
} from '../../components/subscriptionModel';
import { Empty, PageBody, PageSkeleton, StaleBanner, fullDate, n0, plural, useToast } from '../kit';

const PLAN_ORDER: PlanId[] = ['pilot', 'basic', 'premium', 'enterprise'];
type Modal =
  | { kind: 'upgrade'; plan: 'premium' | 'enterprise'; name: string }
  | { kind: 'ask'; title: string; body: React.ReactNode; message: string }
  | { kind: 'cancel' }
  | { kind: 'branch' };

const Body: React.FC<{ children: React.ReactNode }> = ({ children }) => <p style={{ margin: 0, fontSize: 16, lineHeight: '24px' }}>{children}</p>;

/* Same add-ons, keys and wording as the classic SubscriptionManager. */
const ADDONS: { key: 'gps' | 'fuel' | 'fees'; label: string; desc: string; schoolOnly?: boolean }[] = [
  { key: 'gps', label: 'GPS Tracking', desc: 'Live location on every vehicle.' },
  { key: 'fuel', label: 'Fuel Monitoring', desc: 'Track consumption and flag siphoning.' },
  { key: 'fees', label: 'School Fees', desc: 'Collect and reconcile fees in-app.', schoolOnly: true },
];

const PLAN_NAME: Record<string, string> = { pilot: 'Pilot', basic: 'Basic', premium: 'Premium', enterprise: 'Enterprise' };

const Billing: React.FC = () => {
  const f = useFleet();
  const sub = useOrgSubscription(f.org.code || null);
  const raw = f.org.raw as Record<string, unknown>;

  const isSchool = f.org.productType !== 'CARGO' && f.org.productType !== 'SELF_TRACKING';
  const unit: AccountUnit = isSchool ? 'child' : 'vehicle';
  const size = Number(isSchool ? raw.plannedChildren : raw.plannedBuses) || 0;
  const storedPlan = (String(raw.subscriptionPlan || raw.plan || '').trim().toLowerCase() || 'premium') as PlanId;
  const currentPeriod = ((raw.billingPeriod as BillingPeriod) || (isSchool ? 'termly' : 'month'));
  const addons = (raw.addons as Record<string, { wanted?: boolean; status?: string }> | undefined) || {};
  const addonCount = Object.values(addons).filter((a) => a && a.wanted).length;
  const cancelling = raw.cancelAtPeriodEnd === true;
  const exempt = raw.subscriptionExempt === true;

  const cycles: { id: BillingPeriod; label: string }[] = isSchool
    ? [{ id: 'termly', label: 'Per term' }, { id: 'month', label: 'Monthly' }, { id: 'year', label: 'Yearly · save 10%' }]
    : [{ id: 'month', label: 'Monthly' }, { id: 'year', label: 'Yearly · save 10%' }];
  // Follows the org's own cycle until the user picks another one.
  const [picked, setCycle] = useState<BillingPeriod | null>(null);
  const cycle: BillingPeriod = picked ?? (cycles.some((c) => c.id === currentPeriod) ? currentPeriod : cycles[0].id);

  const { toast } = useToast();
  const { refreshAdminStatus } = useAuth();
  const [modal, setModal] = useState<Modal | null>(null);
  const [resuming, setResuming] = useState(false);
  const [branches, setBranches] = useState<BranchOrgRow[] | null>(null);
  const [switching, setSwitching] = useState<string | null>(null);
  const enterprise = storedPlan === 'enterprise';
  useEffect(() => {
    if (!enterprise || !f.org.code) return;
    let live = true;
    listBranches(f.org.code).then((r) => {
      if (!live) return;
      setBranches(r.ok && Array.isArray(r.orgs) ? r.orgs : []);
      // The profile menu's "Switch branch" lands here.
      if (window.location.hash === '#branches') window.requestAnimationFrame(() => document.getElementById('branches')?.scrollIntoView({ block: 'start', behavior: 'smooth' }));
    });
    return () => { live = false; };
  }, [enterprise, f.org.code, modal]);

  const planName = PLAN_NAME[storedPlan] || storedPlan.charAt(0).toUpperCase() + storedPlan.slice(1);
  const school = `${f.org.name || 'Our school'} (${f.org.code})`;
  const renewal = sub.expirationDate ? ` on ${fullDate(sub.expirationDate)}` : '';
  const cycleWord = (c: BillingPeriod) => (c === 'termly' ? 'per term' : c === 'month' ? 'monthly' : 'yearly');

  const choosePlan = (id: PlanId, name: string) => {
    const up = PLAN_ORDER.indexOf(id) > PLAN_ORDER.indexOf(storedPlan) && !(storedPlan === 'basic' && id === 'premium');
    if (up && (id === 'premium' || id === 'enterprise')) { setModal({ kind: 'upgrade', plan: id, name }); return; }
    setModal({
      kind: 'ask', title: `Move to ${name}`,
      body: <Body>Trasse makes this change for you. It starts at your next renewal{renewal}, and from then you pay the {name} rate. Until then nothing changes.</Body>,
      message: `Please move ${school} from ${planName} to ${name} at our next renewal.`,
    });
  };

  const resume = () => {
    if (resuming) return;
    setResuming(true);
    resumeSubscription(f.org.code)
      .then((r) => toast(r.ok ? { kind: 'success', text: 'Your subscription carries on.' } : { kind: 'error', text: r.message || 'Couldn’t undo the cancellation. Try again.' }))
      .finally(() => setResuming(false));
  };

  const openBranch = (o: BranchOrgRow) => {
    if (switching) return;
    setSwitching(o.code);
    switchBranch(o.code)
      .then(async (r) => {
        if (!r.ok) { toast({ kind: 'error', text: r.message || 'Couldn’t open that branch.' }); return; }
        await refreshAdminStatus();
        toast({ kind: 'success', text: `You’re in ${r.name || o.name}` });
      })
      .finally(() => setSwitching(null));
  };
  const planDesc = PLAN_DEFS.find((p) => p.id === storedPlan)?.desc || (storedPlan === 'basic' ? 'Live tracking and alerts.' : '');
  // An Enterprise school pays by its place in its group (750 on the first 3 campuses, 850 from the 4th); the server's list carries it.
  const campus = branches?.find((o) => o.code === f.org.code)?.campus || 1;
  const nextAmount = storedPlan !== 'pilot' && size > 0 ? bill(storedPlan, size, unit, currentPeriod, addonCount, campus) : null;
  const priceFor = (p: PlanId) => (size > 0 ? `Ksh ${n0(bill(p, size, unit, cycle, addonCount, campus))}` : `Ksh ${n0(rate(p, unit, { size: 1, campus }))}`);
  const per = size > 0 ? `per ${({ term: 'term', quarter: 'quarter', mo: 'month', yr: 'year' } as Record<string, string>)[totalSuffix(unit, cycle)] || 'term'}` : `per ${unitWord(unit, 1)} per ${perUnitSuffix(unit) === 'mo' ? 'month' : 'term'}${cycle === 'year' ? ', 10% off paid yearly' : ''}`;

  usePage({
    key: 'ConsoleBilling', title: 'Plan and billing',
    zanaContext: `Billing · ${planName}`,
    zanaIntro: 'I can explain your plan and compare plans.',
    prompts: [isSchool ? 'What will next term cost?' : 'What will next month cost?', 'Is yearly worth it for us?', 'What does Enterprise add?'],
  });

  const statusChip = sub.status === 'locked'
    ? <span className="chip c-bad">Locked · payment overdue</span>
    : sub.paymentPending ? <span className="chip c-warn">Payment failed · retry</span>
      : sub.isPilot ? <span className="chip c-teal">Pilot · {plural(sub.pilotDaysLeft, 'day')} left</span>
        : cancelling ? <span className="chip c-warn">Ends at renewal</span>
          : sub.status === 'grace' ? <span className="chip c-warn">Renews in {plural(Math.max(0, sub.daysLeft), 'day')}</span>
            : <span className="chip c-teal">Current plan</span>;

  const counts = `${plural(f.childrenTotal, 'child', 'children')} on ${plural(f.buses.length, 'bus', 'buses')}`;

  return (
    <PageBody loading={f.loading || sub.status === 'loading'} skeleton={<PageSkeleton stats />}>
      <div className={'wrap' + (f.error ? ' stale' : '')} style={{ maxWidth: 1040 }}>
        {f.error && <StaleBanner what="plan" since={f.updatedAt} onRetry={f.retry} />}
        <div className="phead">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <h1 className="h1">Plan and billing</h1>
            <p className="sub">{f.org.name || 'Your school'} {exempt ? 'is billed by Trasse directly, not through the app' : `pays ${billedWord(unit, currentPeriod)}`}. {counts}.</p>
          </div>
        </div>

        <section className="panel" style={{ padding: '22px 24px', display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            {statusChip}
            <h2 className="h2" style={{ fontSize: 26, marginTop: 8 }}>{planName}</h2>
            {planDesc && <p className="sub">{planDesc}</p>}
          </div>
          {sub.expirationDate && !exempt && (
            <div>
              <span className="sm" style={{ color: 'var(--muted)' }}>{cancelling ? 'Access ends' : sub.isPilot ? 'Pilot ends' : sub.status === 'locked' ? 'Expired' : 'Next invoice'}</span>
              {nextAmount != null && !cancelling && !sub.isPilot
                ? <div className="big"><span className="unit">Ksh</span>{n0(nextAmount)}</div>
                : <div className="big">{fullDate(sub.expirationDate)}</div>}
              <span className="sm" style={{ color: 'var(--muted)' }}>
                {nextAmount != null && !cancelling && !sub.isPilot ? `${fullDate(sub.expirationDate)} · at your plan’s rate` : cancelling ? 'You won’t be billed again' : ''}
              </span>
            </div>
          )}
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            {!exempt && <a className={'btn ' + (sub.status === 'locked' || sub.paymentPending ? 'b-ink' : 'b-line')} href={`/pay?org=${encodeURIComponent(f.org.code)}`}>{sub.status === 'locked' || sub.paymentPending ? 'Pay now' : 'Pay early'}</a>}
            {/* Billed by Trasse directly: every change is a request to Trasse (2026-10-28). */}
            {exempt && (
              <>
                <button type="button" className="btn b-line" onClick={() => setModal({
                  kind: 'ask', title: 'Your plan',
                  body: <Body>{f.org.name || 'Your school'} is billed by Trasse directly, so plan changes, invoices and receipts go through us. Send a message and we’ll sort it out.</Body>,
                  message: `Hello Trasse — a question about the plan for ${school}.`,
                })}>Talk to Trasse</button>
                <button type="button" className="btn b-ghost" onClick={() => setModal({
                  kind: 'ask', title: 'Cancel the subscription?',
                  body: <Body>Trasse bills {f.org.name || 'your school'} directly, so we cancel it for you. Everything keeps working until the end of the period you’ve paid for.</Body>,
                  message: `Please cancel the Trasse subscription for ${school}.`,
                })}>Cancel subscription</button>
              </>
            )}
            {!exempt && !sub.isPilot && (cancelling
              ? <button type="button" className="btn b-line" onClick={resume} disabled={resuming}>{resuming ? 'Saving…' : 'Keep my subscription'}</button>
              : sub.status !== 'locked' && <button type="button" className="btn b-ghost" onClick={() => setModal({ kind: 'cancel' })}>Cancel subscription</button>)}
          </div>
        </section>

        <div className="seg" style={{ alignSelf: 'flex-start' }} role="group" aria-label="Billing cycle">
          {cycles.map((c) => (
            <button key={c.id} type="button" className={cycle === c.id ? 'on' : ''} aria-pressed={cycle === c.id} onClick={() => setCycle(c.id)}>{c.label}</button>
          ))}
        </div>

        <div className="two" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 20 }}>
          {SELLABLE_PLAN_DEFS.map((p) => {
            const mine = p.id === storedPlan;
            return (
              <section key={p.id} className="panel" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14, ...(mine ? { boxShadow: 'inset 0 0 0 2px var(--ink)' } : {}) }}>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <b style={{ fontSize: 16 }}>{p.name}</b>
                  {mine ? <span className="chip c-mute">Your plan</span> : p.id === 'enterprise' ? <span className="sm" style={{ color: 'var(--muted)' }}>For school groups</span> : null}
                </div>
                <div>
                  <span className="big" style={{ fontSize: 44, lineHeight: '46px' }}>{priceFor(p.id)}</span>{' '}
                  <span className="sub">{per}</span>
                </div>
                <ul className="zfacts">
                  {p.feats.map((x) => <li key={x}>{x}</li>)}
                </ul>
                {!mine && <button type="button" className="btn b-ink" style={{ alignSelf: 'flex-start' }} onClick={() => (exempt ? setModal({
                  kind: 'ask', title: `Move to ${p.name}`,
                  body: <Body>Trasse bills {f.org.name || 'your school'} directly, so we make the change and update your invoice.</Body>,
                  message: `Please move ${school} from ${planName} to ${p.name}.`,
                }) : choosePlan(p.id, p.name))}>{p.id === 'enterprise' ? 'Move to Enterprise' : `Change to ${p.name}`}</button>}
              </section>
            );
          })}
        </div>
        <p className="sm" style={{ margin: 0, color: 'var(--faint)' }}>
          {size > 0
            ? `Prices for the ${plural(size, unitWord(unit, 1), unitWord(unit, 2))} on your plan${addonCount ? ` with ${plural(addonCount, 'add-on')}` : ''}. Moving up starts at once. Moving down starts at your next renewal.`
            : 'Your plan has no size on file, so prices are shown per ' + unitWord(unit, 1) + '. Moving up starts at once. Moving down starts at your next renewal.'}
        </p>
        {!exempt && cycle !== currentPeriod && (
          <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
            <span className="sm" style={{ color: 'var(--muted)' }}>You pay {cycleWord(currentPeriod)} now.</span>
            <button type="button" className="btn s b-line" onClick={() => setModal({
              kind: 'ask', title: `Pay ${cycleWord(cycle)}`,
              body: <Body>Trasse switches how often you pay from your next renewal{renewal}. Your plan stays the same.</Body>,
              message: `Please switch ${school} to paying ${cycleWord(cycle)}, from our next renewal.`,
            })}>Pay {cycleWord(cycle)} instead</button>
          </div>
        )}

        <section className="panel">
          <div className="ph"><h2 className="h2">Add-ons</h2></div>
          {ADDONS.filter((a) => isSchool || !a.schoolOnly).map((a) => {
            const st = addons[a.key];
            const on = !!st?.wanted;
            const pending = on && !!st?.status && st.status !== 'active' && st.status !== 'on';
            return (
              <div key={a.key} className="todo">
                <div style={{ flex: 1 }}>
                  <b>{a.label}</b>
                  <p className="sm" style={{ margin: '2px 0 0', color: 'var(--muted)' }}>{a.desc} Ksh {ADDON_PRICE} per {unitWord(unit, 1)} per {perUnitSuffix(unit) === 'mo' ? 'month' : 'term'}.</p>
                </div>
                {on
                  ? <span className={'chip ' + (pending ? 'c-warn' : 'c-ok')}>{pending ? `Requested · ${String(st?.status).replace(/_/g, ' ')}` : 'On'}</span>
                  : <button type="button" className="btn s b-line" onClick={() => setModal({
                    kind: 'ask', title: `Add ${a.label}`,
                    body: <Body>{a.desc} Ksh {ADDON_PRICE} per {unitWord(unit, 1)} per {perUnitSuffix(unit) === 'mo' ? 'month' : 'term'}. Trasse sets it up with you.</Body>,
                    message: `We would like to add ${a.label} for ${school}.`,
                  })}>Add</button>}
              </div>
            );
          })}
        </section>

        {enterprise && (
          <section className="panel" id="branches">
            <div className="ph">
              <h2 className="h2">Branches</h2>
              <button type="button" className="btn s b-ink" onClick={() => setModal({ kind: 'branch' })}>Add a branch</button>
            </div>
            {branches === null && <p className="sm" style={{ margin: 0, padding: '0 20px 18px', color: 'var(--muted)' }}>Loading…</p>}
            {branches !== null && branches.length < 2 && (
              <p className="sm" style={{ margin: 0, padding: '0 20px 18px', color: 'var(--muted)' }}>Only this school so far. A branch gets its own buses, drivers, parents and logins, and renews with this one.</p>
            )}
            {branches !== null && branches.length >= 2 && branches.map((o) => (
              <div key={o.code} className="todo">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <b>{o.name}</b>
                  <p className="sm" style={{ margin: '2px 0 0', color: 'var(--muted)' }}><span className="mono">{o.code}</span>{o.isMother ? ' · Head office' : ''}</p>
                </div>
                {o.code === f.org.code
                  ? <span className="chip c-mute">You’re here</span>
                  : <button type="button" className="btn s b-line" disabled={!!switching} onClick={() => openBranch(o)}>{switching === o.code ? 'Opening…' : 'Open'}</button>}
              </div>
            ))}
          </section>
        )}

        <section className="panel" style={{ overflow: 'hidden' }}>
          <div className="ph"><h2 className="h2">Invoices</h2></div>
          <Empty title="Invoices aren’t listed here yet"
            body="The console can’t fetch past invoices. For a copy of an invoice or a receipt, contact support with your school code.">
            <a className="btn s b-line" href="/support">Contact support</a>
          </Empty>
        </section>
        {modal?.kind === 'upgrade' && <UpgradeModal orgCode={f.org.code} plan={modal.plan} planName={modal.name} onClose={() => setModal(null)} />}
        {modal?.kind === 'ask' && <AskTrasseModal title={modal.title} body={modal.body} message={modal.message} onClose={() => setModal(null)} />}
        {modal?.kind === 'cancel' && <CancelModal orgCode={f.org.code} accessEnds={sub.expirationDate || null} onClose={() => setModal(null)} />}
        {modal?.kind === 'branch' && <BranchModal orgCode={f.org.code} unit={unit} onClose={() => setModal(null)} />}
      </div>
    </PageBody>
  );
};

export default Billing;
